"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.shiftEndDateByStartDateChange = exports.getDateWithFutureDayOfMonth = exports.getDateWithOffset = exports.timeDifferenceInWords = exports.calculateTimeDifference = exports.calculateDaysDifference = exports.calculateBirthDate = exports.addTimeFrame = exports.subtractFromDateHours = exports.subtractFromDateMinutes = exports.subtractFromDateYears = exports.getAddOrSubtractTimeUnit = exports.calculateEndDates = exports.subtractFromDateMonths = exports.subtractFromDateDays = exports.subtractFromDate = exports.addToDateHours = exports.addToDateMinutes = exports.addToDateYears = exports.addToDateMonths = exports.addToDateDays = exports.addToDate = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
const duration_1 = __importDefault(require("dayjs/plugin/duration"));
const timezone_1 = __importDefault(require("dayjs/plugin/timezone"));
const utc_1 = __importDefault(require("dayjs/plugin/utc"));
const types_1 = require("../types");
const validation_1 = require("../validation");
const formatter_1 = require("../formatter");
const constants_1 = require("../types/constants");
dayjs_1.default.extend(duration_1.default);
dayjs_1.default.extend(timezone_1.default);
dayjs_1.default.extend(utc_1.default);
/**
 * Adds a specified amount of time to a given date.
 *
 * @param {DateInput} date - The initial date to which the time will be added. Can be a Date object or a string that can be parsed by dayjs.
 * @param {number} amount - The amount of time to add. Must be a positive number.
 * @param {dayjs.ManipulateType} unit - The unit of time to add (e.g., 'day', 'month', 'year'). Must be a valid dayjs.ManipulateType.
 * @param {string} [timezone] - Optional. The timezone in which the date manipulation should be performed. If not provided, the local timezone is used.
 * @returns {Date} A new Date object with the specified amount of time added.
 */
function addToDate(date, amount, unit, timezone) {
    if (!(0, validation_1.isValidDate)(date)) {
        throw new types_1.CustomError(types_1.errorCode.invalidDate, 'Invalid date provided');
    }
    const dateInstance = typeof timezone === 'string' && timezone.trim() ? dayjs_1.default.tz(date, timezone) : (0, dayjs_1.default)(date);
    if (typeof amount === 'number' && amount > 0) {
        return dateInstance.add(amount, unit).toDate();
    }
    return dateInstance.toDate();
}
exports.addToDate = addToDate;
/**
 * Adds a specified amount of days to a date.
 * @param {Date | string} date - The initial date.
 * @param {number} days - The number of days to add.
 * @returns {Date} The resulting date.
 */
function addToDateDays(date, days, timezone) {
    return addToDate(date, days, types_1.TimeUnit.DAY, timezone);
}
exports.addToDateDays = addToDateDays;
/**
 * Adds a specified amount of months to a date.
 * @param {Date | string} date - The initial date.
 * @param {number} months - The number of months to add.
 * @returns {Date} The resulting date.
 */
function addToDateMonths(date, months) {
    return addToDate(date, months, types_1.TimeUnit.MONTH);
}
exports.addToDateMonths = addToDateMonths;
/**
 * Adds a specified amount of years to a date.
 * @param {Date | string} date - The initial date.
 * @param {number} years - The number of years to add.
 * @returns {Date} The resulting date.
 */
function addToDateYears(date, years) {
    return addToDate(date, years, types_1.TimeUnit.YEAR);
}
exports.addToDateYears = addToDateYears;
/**
 * Adds a specified amount of minutes to a date.
 * @param {Date | string} date - The initial date.
 * @param {number} minutes - The number of minutes to add.
 * @returns {Date} The resulting date.
 */
function addToDateMinutes(date, minutes) {
    return addToDate(date, minutes, types_1.TimeUnit.MINUTE);
}
exports.addToDateMinutes = addToDateMinutes;
/**
 * Adds a specified amount of hours to a date.
 * @param {Date | string} date - The initial date.
 * @param {number} hours - The number of hours to add.
 * @returns {Date} The resulting date.
 */
function addToDateHours(date, hours) {
    return addToDate(date, hours, types_1.TimeUnit.HOUR);
}
exports.addToDateHours = addToDateHours;
/**
 * Subtracts a specified amount of time from a date.
 *
 * @param {Date | string} date - The initial date.
 * @param {number} amount - The amount to subtract (positive values only).
 * @param {dayjs.ManipulateType} unit - The unit of time to subtract (e.g., 'day', 'month').
 * @returns {Date} The resulting date.
 */
function subtractFromDate(date, amount, unit) {
    if (typeof amount === 'number' && amount > 0) {
        return (0, dayjs_1.default)(date).subtract(amount, unit).toDate();
    }
    return date instanceof Date ? date : (0, dayjs_1.default)(date).toDate();
}
exports.subtractFromDate = subtractFromDate;
/**
 * Subtracts a specified amount of days from a date.
 * @param {Date | string} date - The initial date.
 * @param {number} days - The number of days to subtract.
 * @returns {Date} The resulting date.
 */
function subtractFromDateDays(date, days) {
    return subtractFromDate(date, days, types_1.TimeUnit.DAY);
}
exports.subtractFromDateDays = subtractFromDateDays;
/**
 * Subtracts a specified amount of months from a date.
 * @param {Date | string} date - The initial date.
 * @param {number} months - The number of months to subtract.
 * @returns {Date} The resulting date.
 */
function subtractFromDateMonths(date, months) {
    return subtractFromDate(date, months, types_1.TimeUnit.MONTH);
}
exports.subtractFromDateMonths = subtractFromDateMonths;
/**
 * Subtracts a specified amount of years from a date.
 * @param {Date | string} date - The initial date.
 * @param {number} years - The number of years to subtract.
 * @returns {Date} The resulting date.
 */
function subtractFromDateYears(date, years) {
    return subtractFromDate(date, years, types_1.TimeUnit.YEAR);
}
exports.subtractFromDateYears = subtractFromDateYears;
/**
 * Subtracts a specified amount of minutes from a date.
 * @param {Date | string} date - The initial date.
 * @param {number} minutes - The number of minutes to subtract.
 * @returns {Date} The resulting date.
 */
function subtractFromDateMinutes(date, minutes) {
    return subtractFromDate(date, minutes, types_1.TimeUnit.MINUTE);
}
exports.subtractFromDateMinutes = subtractFromDateMinutes;
/**
 * Subtracts a specified amount of hours from a date.
 * @param {Date | string} date - The initial date.
 * @param {number} hours - The number of hours to subtract.
 * @returns {Date} The resulting date.
 */
function subtractFromDateHours(date, hours) {
    return subtractFromDate(date, hours, types_1.TimeUnit.HOUR);
}
exports.subtractFromDateHours = subtractFromDateHours;
const amountMap = {
    [types_1.RepetitionUnit.DAY]: 1,
    [types_1.RepetitionUnit.WEEK]: 7,
    [types_1.RepetitionUnit.MONTH]: 1,
    [types_1.RepetitionUnit.QUARTER]: 3,
    [types_1.RepetitionUnit.YEAR]: 1,
};
/**
 * Adds a specified time frame to a date based on the given time frame and multiplier.
 *
 * @param {Date | string} date - The initial date.
 * @param {PaymentPlanRepetition} timeFrame - The time frame to add (e.g., 'day', 'month').
 * @param {number} [multiplier=1] - The multiplier for the time frame.
 * @returns {Date} The resulting date with the added time frame.
 * @throws {Error} Throws an error if the time frame is invalid or if multiplier is not a positive number.
 */
function addTimeFrame(date, timeFrame, multiplier = 1) {
    if (multiplier <= 0) {
        throw new types_1.CustomError(types_1.errorCode.invalidMutiplier, `Multiplier must be a positive number. Provided: ${multiplier}`);
    }
    if (!Object.values(types_1.RepetitionUnit).includes(timeFrame)) {
        throw new types_1.CustomError(types_1.errorCode.invalidTimeFrame, `Invalid time frame: ${timeFrame}`);
    }
    const amount = amountMap[timeFrame] * multiplier;
    const unit = timeFrame === types_1.RepetitionUnit.DAY || timeFrame === types_1.RepetitionUnit.WEEK
        ? 'days'
        : timeFrame === types_1.RepetitionUnit.MONTH || timeFrame === types_1.RepetitionUnit.QUARTER
            ? 'months'
            : 'years';
    return (0, dayjs_1.default)(date).add(amount, unit).toDate();
}
exports.addTimeFrame = addTimeFrame;
// Map the unit character to TimeUnit
const unitMap = {
    y: types_1.TimeUnit.YEAR,
    m: types_1.TimeUnit.MONTH,
    d: types_1.TimeUnit.DAY,
    h: types_1.TimeUnit.HOUR,
    n: types_1.TimeUnit.MINUTE,
};
/**
 * Calculates the birth date to compare to for age validation.
 * @param startDate - The event's start date.
 * @param requiredAge - The user's required age in the format "number+unit" (e.g., "18y" or "5m").
 * @param isAgeIncluded - Whether the age should be included or not.
 * @returns The calculated birth date.
 */
function calculateBirthDate(startDate, requiredAge, isAgeIncluded) {
    // Extract the unit and value from the requiredAge string
    const ageUnitChar = requiredAge.charAt(requiredAge.length - 1);
    const LAST_CHARACTER_INDEX = -1;
    let ageValue = parseInt(requiredAge.slice(0, LAST_CHARACTER_INDEX), 10);
    const ageUnit = unitMap[ageUnitChar];
    if (isNaN(ageValue) || ageValue < 0) {
        throw new types_1.CustomError(types_1.errorCode.invalidMutiplier, `Invalid age value: ${requiredAge}`);
    }
    if (isAgeIncluded) {
        ageValue += 1;
    }
    // Calculate the new date based on the unit
    switch (ageUnit) {
        case types_1.TimeUnit.YEAR:
            return subtractFromDateYears(startDate, ageValue);
        case types_1.TimeUnit.MONTH:
            return subtractFromDateMonths(startDate, ageValue);
        case types_1.TimeUnit.DAY:
            return subtractFromDateDays(startDate, ageValue);
        case types_1.TimeUnit.HOUR:
            return subtractFromDateHours(startDate, ageValue);
        case types_1.TimeUnit.MINUTE:
            return subtractFromDateMinutes(startDate, ageValue);
        default:
            throw new types_1.CustomError(types_1.errorCode.invalidTimeFrame, `Unsupported time unit: ${ageUnitChar}`);
    }
}
exports.calculateBirthDate = calculateBirthDate;
/**
 * Determines the appropriate function to add or subtract a specified time unit.
 *
 * @param timeUnit - The unit of time to be added or subtracted (e.g., month, year).
 * @param add - Optional boolean indicating whether to add (true) or subtract (false) the time unit. Defaults to false.
 * @returns The corresponding dayjs operation unit type for adding or subtracting the specified time unit.
 * @throws CustomError if the provided time unit is invalid.
 */
function getAddOrSubtractTimeUnit(timeUnit, add) {
    switch (timeUnit) {
        case types_1.TimeUnit.DAY:
            return add ? addToDateDays : subtractFromDateDays;
        case types_1.TimeUnit.HOUR:
            return add ? addToDateHours : subtractFromDateHours;
        case types_1.TimeUnit.MINUTE:
            return add ? addToDateMinutes : subtractFromDateMinutes;
        case types_1.TimeUnit.MONTH:
            return add ? addToDateMonths : subtractFromDateMonths;
        case types_1.TimeUnit.YEAR:
            return add ? addToDateYears : subtractFromDateYears;
        default:
            throw new types_1.CustomError(types_1.errorCode.invalidTimeFrame, 'Invalid time frame');
    }
}
exports.getAddOrSubtractTimeUnit = getAddOrSubtractTimeUnit;
/**
 * Calculates end dates by adding or subtracting a specified duration from multiple input dates.
 *
 * @param {string[]} dates - An array of input dates to calculate the end dates for.
 * @param {IDuration} duration - The duration object containing a unit (e.g., "days", "months") and an amount.
 * @param {boolean} [addToDate=true] - Determines whether to add (`true`) or subtract (`false`) the duration.
 * @returns {string[]} - An array of calculated end dates in locale string format.
 *
 * @example
 * const duration = { unit: 'days', amount: 7 };
 * const results = calculateEndDates(['2024-11-01', '2024-11-10'], duration, true);
 * console.log(results);
 * // Output: ['11/08/2024', '11/17/2024']
 */
function calculateEndDates(dates, duration, addToDate = true) {
    const relevantFunction = getAddOrSubtractTimeUnit(duration.unit, addToDate);
    return dates.map(date => relevantFunction(date, duration.amount).toLocaleDateString());
}
exports.calculateEndDates = calculateEndDates;
/**
 * Calculates the number of days between two dates.
 *
 * @param date - The date to compare against.
 * @param dateToCalculateFrom - The second date to compare to (defaults to the current date if not provided).
 * @returns - The number of days between the two dates, rounded up.
 */
function calculateDaysDifference(date, dateToCalculateFrom) {
    const plannedDate = (0, dayjs_1.default)(date);
    const currentDate = (0, dayjs_1.default)(dateToCalculateFrom);
    if (!plannedDate.isValid() || !currentDate || !currentDate.isValid()) {
        throw new types_1.CustomError(types_1.errorCode.invalidTimeFrame, 'Invalid date provided');
    }
    // Calculate the difference in days and round up
    const diff = currentDate.diff(plannedDate, types_1.TimeUnit.DAY, true);
    return Math.ceil(diff);
}
exports.calculateDaysDifference = calculateDaysDifference;
/**
 * Calculates the difference between two dates based on the provided time unit.
 *
 * @param date - The date to compare against (in ISO format).
 * @param dateToCalculateFrom - The second date to compare to (defaults to the current date if not provided).
 * @param unit - The time unit for the difference calculation (default is `TimeUnit.DAY`).
 *              Can be one of the following: `TimeUnit.YEAR`, `TimeUnit.MONTH`, `TimeUnit.DAY`,
 *              `TimeUnit.HOUR`, `TimeUnit.MINUTE`, or `TimeUnit.SECOND`.
 * @returns - The difference between the two dates, rounded up for `TimeUnit.YEAR` and `TimeUnit.MONTH`
 *            if the fractional part exceeds 0.5; For 'TimeUnit.DAY' the difference is floored.
 *            otherwise, returns rounded whole numbers for other units.
 * @throws {CustomError} - Throws an error if the provided dates are invalid.
 */
function calculateTimeDifference(date, dateToCalculateFrom, unit = types_1.TimeUnit.DAY) {
    const plannedDate = (0, dayjs_1.default)(date).utc();
    const currentDate = dateToCalculateFrom != null && (0, validation_1.isValidDate)(dateToCalculateFrom) ? (0, dayjs_1.default)(dateToCalculateFrom) : (0, dayjs_1.default)();
    if (!plannedDate.isValid() || !currentDate.isValid()) {
        throw new types_1.CustomError(types_1.errorCode.invalidTimeFrame, 'Invalid date provided');
    }
    const roundBasedOnUnit = (diff, unit, plannedDate) => {
        if (unit === types_1.TimeUnit.YEAR || unit === types_1.TimeUnit.MONTH) {
            const anniversaryPassed = currentDate.month() > plannedDate.month() ||
                (currentDate.month() === plannedDate.month() && currentDate.date() >= plannedDate.date());
            if (unit === types_1.TimeUnit.YEAR && !anniversaryPassed) {
                diff--; // Adjust for years if the anniversary hasn't passed
            }
            else if (unit === types_1.TimeUnit.MONTH && !anniversaryPassed) {
                diff--; // Adjust for months if the month anniversary hasn't passed
            }
            return Math.floor(diff) + (diff - Math.floor(diff) > types_1.ROUNDING_THRESHOLD ? 1 : 0); // Round up if > ROUNDING_THRESHOLD
        }
        else if (unit === types_1.TimeUnit.DAY) {
            return Math.floor(diff); // Default flooring for days
        }
        else {
            return Math.round(diff); // Default rounding for smaller units
        }
    };
    // Calculate the difference based on the provided unit
    const diff = currentDate.diff(plannedDate, unit, true); // Get fractional difference
    return roundBasedOnUnit(diff, unit, plannedDate);
}
exports.calculateTimeDifference = calculateTimeDifference;
/**
 * Returns a string representing the time difference between two dates in words.
 *
 * @param {string} startDate - The start date in ISO format.
 * @param {string} endDate - The end date in ISO format.
 * @param {TimeUnitLabels} [labels={}] - Optional labels for time units.
 * @param {string} [separator=', '] - Optional separator for the parts of the time difference string.
 * @returns {string} A string representing the time difference between the two dates in words.
 */
const timeDifferenceInWords = (startDate, endDate, labels = {}, separator = ', ') => {
    // Use UTC to avoid timezone issues
    const start = (0, dayjs_1.default)(startDate).utc();
    const end = (0, dayjs_1.default)(endDate).utc();
    let remainingDuration = dayjs_1.default.duration(end.diff(start));
    const parts = [];
    // Calculate months and return them (the sub-month math is too fuzzy to work well)
    const months = remainingDuration.months();
    if (months > 0) {
        return (0, formatter_1.suffix)(months, (0, formatter_1.getUnitPrefix)(types_1.TimeUnit.MONTH, labels));
    }
    // Calculate weeks from remaining days and subtract
    const totalDays = remainingDuration.days();
    const weeks = Math.floor(totalDays / constants_1.DAYS_IN_WEEK);
    if (weeks > 0) {
        parts.push((0, formatter_1.suffix)(weeks, (0, formatter_1.getUnitPrefix)(types_1.TimeUnit.WEEK, labels)));
        remainingDuration = remainingDuration.subtract(weeks * constants_1.DAYS_IN_WEEK, 'days');
    }
    // Calculate remaining days and subtract
    const days = remainingDuration.days();
    if (days > 0) {
        parts.push((0, formatter_1.suffix)(days, (0, formatter_1.getUnitPrefix)(types_1.TimeUnit.DAY, labels)));
        remainingDuration = remainingDuration.subtract(days, 'days');
    }
    // Calculate hours and subtract
    const hours = remainingDuration.hours();
    if (hours > 0) {
        parts.push((0, formatter_1.suffix)(hours, (0, formatter_1.getUnitPrefix)(types_1.TimeUnit.HOUR, labels)));
        remainingDuration = remainingDuration.subtract(hours, 'hours');
    }
    // Calculate minutes and subtract
    const minutes = remainingDuration.minutes();
    if (minutes > 0) {
        parts.push((0, formatter_1.suffix)(minutes, (0, formatter_1.getUnitPrefix)(types_1.TimeUnit.MINUTE, labels)));
        remainingDuration = remainingDuration.subtract(minutes, 'minutes');
    }
    // Calculate remaining seconds
    const seconds = remainingDuration.seconds();
    if (seconds > 0 || parts.length === 0) {
        parts.push((0, formatter_1.suffix)(seconds, (0, formatter_1.getUnitPrefix)(types_1.TimeUnit.SECOND, labels)));
    }
    return parts.join(separator);
};
exports.timeDifferenceInWords = timeDifferenceInWords;
/**
 * Get the date with the offset.
 * @param date The date to offset.
 * @param timezone The timezone to use (default: 'UTC').
 * @returns The date with the offset.
 */
function getDateWithOffset(date, timezone = constants_1.DEFAULT_TIMEZONE) {
    return addToDate(date, (0, dayjs_1.default)().tz(timezone).utcOffset(), 'minutes');
}
exports.getDateWithOffset = getDateWithOffset;
/**
 * Returns a future date with dayOfMonth as the date.
 * @param date The date to change.
 * @param dayOfMonth the day of the month.
 * @param timezone The timezone to use (default: 'UTC').
 * @returns A new `Date` object with the adjusted day of the month, ensuring the date is in the future.
 * @throws {CustomError} if dayOfMonth is not a leagl value
 */
function getDateWithFutureDayOfMonth(date, dayOfMonth, timezone = constants_1.DEFAULT_TIMEZONE) {
    if (dayOfMonth < constants_1.FIRST_DAY_OF_MONTH || dayOfMonth > constants_1.MAX_DAYS_IN_MONTH) {
        throw new types_1.CustomError(types_1.errorCode.invalidDateRange, `Invalid dayOfMonth provided: ${dayOfMonth}`);
    }
    const newDate = (0, dayjs_1.default)(date).tz(timezone);
    const lastDayOfMonth = newDate.daysInMonth();
    if (dayOfMonth > lastDayOfMonth) {
        return newDate.set('date', lastDayOfMonth).toDate();
    }
    const dateWithSetDayOfMonth = newDate.set('date', dayOfMonth || newDate.date());
    // if the dayOfMonth is before or is today, returning the dayOfMonth of the next month.
    const now = (0, dayjs_1.default)().tz(timezone);
    if (dateWithSetDayOfMonth.isBefore(now, 'date') || dateWithSetDayOfMonth.isSame(now, 'date')) {
        const dateWithNewMonth = dateWithSetDayOfMonth;
        return dateWithNewMonth
            .add(1, types_1.TimeUnit.MONTH)
            .set('date', dayOfMonth > dateWithNewMonth.daysInMonth() ? dateWithNewMonth.daysInMonth() : dayOfMonth)
            .utc(true)
            .toDate();
    }
    return dateWithSetDayOfMonth.utc(true).toDate();
}
exports.getDateWithFutureDayOfMonth = getDateWithFutureDayOfMonth;
/**
 * Shifts the end date by the difference between the start and end dates.
 * @param originalStartDate The original start date.
 * @param originalEndDate The original end date.
 * @returns The shifted end date.
 */
function shiftEndDateByStartDateChange(originalStartDate, originalEndDate, newStartDate) {
    const dayDifference = calculateDaysDifference(originalStartDate, originalEndDate);
    return addToDateDays(newStartDate, dayDifference, constants_1.DEFAULT_TIMEZONE);
}
exports.shiftEndDateByStartDateChange = shiftEndDateByStartDateChange;
//# sourceMappingURL=calculateTime.js.map